import { Flex, Skeleton, theme } from 'antd';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MeetingChatCardList from './MeetingChatCardList';
import MeetingChatScreen from './MeetingChatScreen';
import SearchChatFilter from './SearchChatFilter';
import EmptyChatImageURL from '../../../assets/image/emptyChat.svg';
import { useSelector, useDispatch } from 'react-redux';
import ChatAPI from '../../../restAPI/ChatAPI';
import SocketService from '../../../service/SocketService';
import Config from '../../../utils/Config';
import { retryApiCall, RETRY_CONFIGS } from '../../../utils/retryUtils';
import { 
    setMeetingChats, 
    chatMessageReceived, 
    chatMessageDeleted,
    chatListUpdate,
    markMessagesAsRead
} from '../../../redux/slices/meetingChatSlice';

export default function MeetingChatLayout() {
    const dispatch = useDispatch();
    const location = useLocation();
    const { userDetailsId, token, companyId } = useSelector(state => state.auth);
    const meetingChats = useSelector(state => state.meetingChat.chats);    
    const [searchTerm, setSearchTerm] = useState('');
    const [chatFilterListData, setChatFilterListData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedChat, setSelectedChat] = useState(-1);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 960);

    const {
        token: {
            colorBgContainer,
            borderRadiusLG,
            colorBorder,
        },
    } = theme.useToken();

    
    useEffect(() => {
        if (!token || !companyId) return;
        
        SocketService.init(Config.BACKEND_URL, token, companyId);
        
        const notificationSound = new Audio('/sounds/notification.mp3');
        
        SocketService.onMessage((message) => {
            console.log('received message : ',message);
            dispatch(chatMessageReceived({ 
                groupId: message.groupId, 
                message 
            }));
        
            notificationSound.play().catch(error => console.error('Error playing sound:', error));
        });
        
        SocketService.onMessageDeleted(({ messageId, groupId }) => {
            console.log('message deleted: ',messageId);
            dispatch(chatMessageDeleted({ messageId, groupId }));
        });
        
        SocketService.onChatListUpdate((update) => {
            console.log('chat list update received:', update);
            dispatch(chatListUpdate({ 
                ...update, 
                userDetailsId,
                selectedChatId: selectedChat 
            }));
        });

        return () => {
            SocketService.cleanup();
        };
    }, [dispatch, token, companyId]);

    useEffect(() => {
        if (!token || !companyId) return;
        if (selectedChat >= 0) {
            SocketService.joinGroup(selectedChat);
        }

        return () => {
            if (selectedChat >= 0 && token && companyId) {                        
                SocketService.leaveGroup(selectedChat);
            }
        };
    }, [selectedChat]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth <= 960);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    
    useEffect(() => {
        if (userDetailsId && companyId) {
            const abortController = new AbortController();

            setSelectedChat(-1);
            setSearchTerm('');
            
            const fetchMeetingGroups = async () => {
                setLoading(true);
                try {
                    const res = await ChatAPI.getUserMeetingGroups(userDetailsId, abortController.signal);
                    if (res.status === 200) {
                        const sortedChats = [...res.data.data].sort((a, b) => {
                            const lastMessageA = a.messages.length ? new Date(a.messages[a.messages.length - 1].createdAt) : new Date(0);
                            const lastMessageB = b.messages.length ? new Date(b.messages[b.messages.length - 1].createdAt) : new Date(0);
                            return lastMessageB - lastMessageA;
                        });
                        dispatch(setMeetingChats(sortedChats));
                        setChatFilterListData(sortedChats);

                        
                        if (location.state?.chatId) {
                            const chatIdFromState = location.state.chatId;
                            const foundChat = sortedChats.find(chat => chat.id === parseInt(chatIdFromState) || chat.id === chatIdFromState);
                            if (foundChat) {
                                setSelectedChat(foundChat.id);
                                setSearchTerm("");                                
                                try {
                                    await retryApiCall(() => ChatAPI.removeUnreadMessages(userDetailsId, foundChat.id), RETRY_CONFIGS.POLLING);
                                    dispatch(markMessagesAsRead({ 
                                        groupId: foundChat.id, 
                                        userDetailsId: userDetailsId 
                                    }));
                                } catch (error) {
                                    console.warn('Failed to mark messages as read:', error.message);
                                }
                            }
                            
                            window.history.replaceState({}, document.title);
                        }
                    }
                } catch (error) {
                    
                    if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
                        console.error('Failed to fetch meeting groups:', error);
                        dispatch(setMeetingChats([]));
                        setChatFilterListData([]);
                    }
                } finally {
                    
                    if (!abortController.signal.aborted) {
                        setLoading(false);
                    }
                }
            };
            fetchMeetingGroups();

            return () => {
                abortController.abort();
            };
        }
    }, [userDetailsId, companyId, dispatch]);

    
    useEffect(() => {
        if (searchTerm) {
            const filterByNameList = meetingChats.filter(obj =>
                obj?.name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
            setChatFilterListData(filterByNameList);
        } else {
            setChatFilterListData(meetingChats);
        }
    }, [searchTerm, meetingChats]);

    const handleChatSelect = async (chatId, chatObj) => {
        setSelectedChat(chatId);
        setSearchTerm("");

        if (userDetailsId) {
            try {
                await retryApiCall(() => ChatAPI.removeUnreadMessages(userDetailsId, chatObj.id), RETRY_CONFIGS.POLLING);
                
                dispatch(markMessagesAsRead({ 
                    groupId: chatObj.id, 
                    userDetailsId:userDetailsId 
                }));
            } catch (error) {
                
                console.warn('Failed to mark messages as read:', error.message);
            }
        }
    };

    const handleBackToChatList = () => {
        setSelectedChat(-1);
    };

    const handleSearchChange = (e) => {
        const searchValue = e.target.value;
        setSearchTerm(searchValue);        
    };

    const chatListContainerStyle = {
        height: 'calc(100svh - 96px)',
        minWidth: 400,
        margin: '16px',
        padding: 24,
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
        overflowY: 'auto',
        width: isMobileView ? '100%' : 'auto',
        display: isMobileView && selectedChat ? 'none' : 'block'
    };

    const chatContainerStyle = {
        height: 'calc(100svh - 96px)',
        margin: '16px',
        borderRadius: borderRadiusLG,
        overflowY: 'auto',
        width: isMobileView ? '100%' : '100%',
        display: isMobileView && !selectedChat ? 'none' : 'block'
    };

    const emptyChatContainer = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        opacity: 0.4,
        flexDirection: 'column',
    };

    const EmptyChatText = {
        marginTop: 10,
        fontSize: 18,
        fontFamily: 'Courier, monospace'
    };

    return (
        <Flex align='flex-start' justify='flex-start'>
            <div style={chatListContainerStyle}>
                <SearchChatFilter value={searchTerm} onChange={handleSearchChange} />
                <div style={{ width: '100%' }}>
                    <Skeleton active loading={loading} style={{ marginTop: 16 }}>
                        <MeetingChatCardList
                            userId={userDetailsId}
                            data={chatFilterListData}
                            onSelect={handleChatSelect}
                            selectedIndex={selectedChat}
                        />
                    </Skeleton>
                </div>
            </div>
            <div style={chatContainerStyle}>
                {selectedChat >= 0 ? (
                    <MeetingChatScreen
                        chat={selectedChat}
                        userId={userDetailsId}
                        onBack={handleBackToChatList}
                    />
                ) : (
                    <div style={emptyChatContainer}>
                        <img src={EmptyChatImageURL} alt='empty chat' style={{ height: '50%' }} />
                        <span style={EmptyChatText}>SELECT CHAT FIRST</span>
                    </div>
                )}
            </div>
        </Flex>
    );
}

